import React from 'react';
import { useContent } from '../../context/ContentContext';
import { createWhatsAppLink } from '../../utils/formatters';
import { resolveMediaUrl } from '../../utils/api';
import { motion } from 'motion/react';
import { MessageCircle, Instagram, MapPin, Clock, Mail, Navigation, Heart } from 'lucide-react';

interface ContactSectionProps {}

export const ContactSection: React.FC<ContactSectionProps> = () => {
  const { data } = useContent();
  const { contactSettings, siteSettings } = data;

  const contactWALink = createWhatsAppLink(
    contactSettings.whatsapp,
    `Halo ${siteSettings.brandName}, saya ingin pesan kopi / info kerjasama event.`
  );

  return (
    <footer
      id="contact"
      className="relative w-full bg-[#FFFFFF] pt-16 sm:pt-24 pb-12 border-t border-[#E2E8F0] overflow-hidden"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Main CTA Heading */}
        <div className="text-center max-w-4xl mx-auto mb-12 sm:mb-14">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F8FAFC] border border-[#E2E8F0] text-[#0056D2] text-xs font-mono tracking-widest uppercase mb-4 shadow-xs"
          >
            <MessageCircle className="w-3.5 h-3.5 text-[#0056D2]" />
            <span>08 — CONNECT WITH LETON</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-display font-black text-3xl sm:text-5xl md:text-6xl text-[#0A192F] tracking-tight uppercase leading-none"
          >
            {contactSettings.title || "LET'S GET COFFEE."}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-3 sm:mt-4 text-sm sm:text-base text-[#64748B] max-w-xl mx-auto"
          >
            {contactSettings.subtitle ||
              'Kunjungi cabang kami atau terhubung langsung melalui WhatsApp dan media sosial.'}
          </motion.p>
        </div>

        {/* Action Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {/* WhatsApp Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="p-6 sm:p-7 rounded-3xl bg-[#FFFFFF] border border-[#E2E8F0] hover:border-[#0056D2]/50 transition-all flex flex-col justify-between group shadow-[0_8px_24px_rgba(10,25,47,0.05)]"
          >
            <div>
              <div className="w-12 h-12 rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0] text-[#0056D2] flex items-center justify-center mb-5 group-hover:bg-[#0A192F] group-hover:text-[#FFFFFF] transition-colors">
                <MessageCircle className="w-6 h-6" />
              </div>
              <h3 className="font-display font-black text-lg sm:text-xl text-[#0A192F]">WHATSAPP OFFICIAL</h3>
              <p className="text-[#64748B] text-xs sm:text-sm mt-2 leading-relaxed font-normal">
                Pemesanan take-away, reservasi, konsultasi menu, dan layanan event.
              </p>
              <p className="font-mono text-sm font-bold text-[#0056D2] mt-4">{contactSettings.whatsapp}</p>
            </div>
            <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
              <a
                href={contactWALink}
                target="_blank"
                rel="noopener noreferrer"
                id="contact-wa-btn"
                className="w-full py-3 px-4 rounded-xl bg-[#0A192F] hover:bg-[#0056D2] text-[#FFFFFF] font-display font-bold text-xs tracking-wider uppercase flex items-center justify-center gap-2 shadow-sm transition-all"
              >
                <MessageCircle className="w-4 h-4 text-[#FFFFFF]" />
                <span>{contactSettings.ctaWhatsappText || 'CHAT VIA WHATSAPP'}</span>
              </a>
            </div>
          </motion.div>

          {/* Instagram Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="p-6 sm:p-7 rounded-3xl bg-[#FFFFFF] border border-[#E2E8F0] hover:border-[#0056D2]/50 transition-all flex flex-col justify-between group shadow-[0_8px_24px_rgba(10,25,47,0.05)]"
          >
            <div>
              <div className="w-12 h-12 rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0] text-[#0056D2] flex items-center justify-center mb-5 group-hover:bg-[#0A192F] group-hover:text-[#FFFFFF] transition-colors">
                <Instagram className="w-6 h-6" />
              </div>
              <h3 className="font-display font-black text-lg sm:text-xl text-[#0A192F]">INSTAGRAM</h3>
              <p className="text-[#64748B] text-xs sm:text-sm mt-2 leading-relaxed font-normal">
                Update chapter terbaru, promo, event komunitas, dan live brewing.
              </p>
              <p className="font-mono text-sm font-bold text-[#0056D2] mt-4">
                {contactSettings.instagramUsername}
              </p>
            </div>
            <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
              <a
                href={contactSettings.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                id="contact-ig-btn"
                className="w-full py-3 px-4 rounded-xl bg-[#0056D2] hover:bg-[#00409E] text-[#FFFFFF] font-display font-bold text-xs tracking-wider uppercase flex items-center justify-center gap-2 shadow-sm transition-all"
              >
                <Instagram className="w-4 h-4 text-[#FFFFFF]" />
                <span>{contactSettings.ctaInstagramText || 'FOLLOW US'}</span>
              </a>
            </div>
          </motion.div>

          {/* Location & Map Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="p-6 sm:p-7 rounded-3xl bg-[#FFFFFF] border border-[#E2E8F0] hover:border-[#0056D2]/50 transition-all flex flex-col justify-between group shadow-[0_8px_24px_rgba(10,25,47,0.05)]"
          >
            <div>
              <div className="w-12 h-12 rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0] text-[#0056D2] flex items-center justify-center mb-5 group-hover:bg-[#0A192F] group-hover:text-[#FFFFFF] transition-colors">
                <MapPin className="w-6 h-6" />
              </div>
              <h3 className="font-display font-black text-lg sm:text-xl text-[#0A192F]">LOKASI UTAMA</h3>
              <p className="text-[#64748B] text-xs sm:text-sm mt-2 leading-relaxed font-normal">
                {contactSettings.address}
              </p>
              <div className="flex items-center gap-2 text-xs text-[#0A192F] mt-4 font-mono font-bold">
                <Clock className="w-3.5 h-3.5 text-[#0056D2]" />
                <span>{contactSettings.openingHours}</span>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
              <a
                href={contactSettings.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                id="contact-maps-btn"
                className="w-full py-3 px-4 rounded-xl bg-[#0A192F] hover:bg-[#0056D2] text-[#FFFFFF] font-display font-bold text-xs tracking-wider uppercase flex items-center justify-center gap-2 shadow-sm transition-all"
              >
                <Navigation className="w-4 h-4 text-[#FFFFFF]" />
                <span>{contactSettings.ctaMapsText || 'GET DIRECTIONS'}</span>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Footer Sub-Bar with Logo */}
        <div className="pt-8 border-t border-[#E2E8F0] flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#64748B]">
          <div className="flex items-center gap-3">
            <div className="relative w-8 h-8 rounded-full overflow-hidden border border-[#E2E8F0] shrink-0 bg-[#FFFFFF]">
              {siteSettings.logoUrl ? (
                <img
                  src={resolveMediaUrl(siteSettings.logoUrl)}
                  alt={siteSettings.brandName}
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <img
                  src="/logo_icon.jpg"
                  alt={siteSettings.brandName || 'LetonKu'}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <p className="font-medium text-[#0A192F]">{contactSettings.footerText}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <a
              href="/privacy-policy"
              onClick={(e) => {
                e.preventDefault();
                try {
                  window.history.pushState(null, '', '/privacy-policy');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                } catch {
                  window.location.href = '/privacy-policy';
                }
              }}
              className="text-[#64748B] hover:text-[#0056D2] hover:underline font-semibold transition-colors"
            >
              Kebijakan Privasi
            </a>
            <span className="text-slate-300 hidden sm:inline">•</span>
            <span className="flex items-center gap-1 text-[#64748B]">
              Crafted with <Heart className="w-3.5 h-3.5 text-[#0056D2] fill-[#0056D2] inline" /> for Leton Community
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

